import React from 'react'
import { Document, Page, Text, View, StyleSheet, Font, Image as PdfImage } from '@react-pdf/renderer'
import HeaderFashionOfPDF from './HeaderFashion'
import FooterOfPDF from '../Footer'
import { ProductData } from '../../../pages/ClientPanel/ProductComposition/RegisterComposition'
import { SelectedSections } from '../../Modals/FashionProduct/GeneratePDFModal'
import { stripHtml } from '../../../utils/stripHtml'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 112,
    paddingBottom: 65,
    paddingHorizontal: 30,
  },
  pageTitle: {
    position: 'absolute',
    top: 68,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  pageNumber: {
    position: 'absolute',
    top: 32,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 9,
    color: '#000',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    backgroundColor: '#f3f4f6',
    padding: 4,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  col: {
    flex: 1,
    paddingRight: 10,
    minWidth: '33%',
  },
  colFull: {
    width: '100%',
    marginBottom: 6,
  },
  label: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#4b5563',
    marginBottom: 2,
  },
  value: {
    fontSize: 10,
    color: '#111827',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginVertical: 10,
  },
  compositionItem: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
  }
})

interface TechnicalSheetPDFProps {
  data: ProductData
  sections: SelectedSections
  gradesByType: any
  gradeTypes: any[]
  coverImageUrl?: string | null
  attachmentImages?: string[]
  extraData?: any
  compositionItems?: any[]
  liveObservations?: string
  liveAnotacoes?: any[]
  liveAprovacoes?: any[]
  liveCores?: any[]
  liveMedidas?: number[]
}

const TechnicalSheetPDF: React.FC<TechnicalSheetPDFProps> = ({ data, sections, gradesByType, gradeTypes, coverImageUrl, attachmentImages, extraData, compositionItems, liveObservations, liveAnotacoes, liveAprovacoes, liveCores, liveMedidas }) => {

  const formatText = (text?: string | null) => {
    if (!text) return '-'
    return stripHtml(text)
  }

  const resolveSizeName = (val: any) => {
    if (typeof val !== 'number' && typeof val !== 'string') return val
    if (!gradesByType) return val
    const allGrades = Object.values(gradesByType).flat()
    const grade = (allGrades as any[]).find(g => g.id === Number(val))
    return grade ? grade.name : val
  }

  const formatSizeRange = (sizeRange: any) => {
    if (!sizeRange) return '-'
    if (Array.isArray(sizeRange)) {
      return sizeRange.map(v => resolveSizeName(v)).join(', ')
    }
    return String(sizeRange).split(',').map(v => resolveSizeName(v.trim())).join(', ')
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderFashionOfPDF />
        
        <Text style={styles.pageTitle} fixed>
          FICHA TÉCNICA - {data.productCode}
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${pageNumber} / ${totalPages}`
        )} fixed />

        {/* SECTION: GERAL (Always visible based on instructions) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INFORMAÇÕES GERAIS</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Código do produto</Text>
              <Text style={styles.value}>{formatText(data.productCode)}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Nome do produto</Text>
              <Text style={styles.value}>{formatText(data.name)}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Produto referência</Text>
              <Text style={styles.value}>{formatText(data.referenceProduct)}</Text>
            </View>
          </View>
          
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Descrição</Text>
              <Text style={styles.value}>{formatText(data.description)}</Text>
            </View>
          </View>

          {extraData && (
            <>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Valor (preço do produto)</Text>
                  <Text style={styles.value}>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(extraData.price || 0)}
                  </Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Coleção vinculada</Text>
                  <Text style={styles.value}>{extraData.collectionName}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Família</Text>
                  <Text style={styles.value}>{extraData.familyName}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Grupo</Text>
                  <Text style={styles.value}>{extraData.groupName}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Episódio</Text>
                  <Text style={styles.value}>{extraData.episodeName}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Tipo de graduação</Text>
                  <Text style={styles.value}>{extraData.gradeTypeName}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Código do fornecedor</Text>
                  <Text style={styles.value}>{extraData.supplierCode}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Identificação do cliente</Text>
                  <Text style={styles.value}>{extraData.clientIdentification}</Text>
                </View>
                <View style={styles.col}></View>
              </View>
            </>
          )}
        </View>

        {/* SECTION: IMAGENS */}
        {sections.imagens && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{(attachmentImages && attachmentImages.length > 1) ? 'IMAGENS DO PRODUTO' : 'IMAGEM DO PRODUTO'}</Text>
            {attachmentImages && attachmentImages.length > 0 ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', marginTop: 10, marginBottom: 10 }}>
                {attachmentImages.map((imageSrc, idx) => (
                  <View key={idx} wrap={false} style={{ width: attachmentImages.length === 1 ? '100%' : '48%', alignItems: 'center', marginBottom: 12, paddingHorizontal: 4 }}>
                    <PdfImage src={imageSrc.startsWith('data:') ? imageSrc : { uri: imageSrc, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }} style={{ width: attachmentImages.length === 1 ? 250 : 200, maxHeight: attachmentImages.length === 1 ? 300 : 240, objectFit: 'contain' }} />
                  </View>
                ))}
              </View>
            ) : coverImageUrl ? (
              <View style={{ alignItems: 'center', marginTop: 10, marginBottom: 10 }}>
                <PdfImage src={coverImageUrl.startsWith('data:') ? coverImageUrl : { uri: coverImageUrl, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }} style={{ width: 250, maxHeight: 300, objectFit: 'contain' }} />
              </View>
            ) : (
              <View style={{ alignItems: 'center', marginTop: 10, marginBottom: 10 }}>
                <Text style={styles.value}>Nenhuma imagem cadastrada.</Text>
              </View>
            )}
          </View>
        )}

        {/* SECTION: CORES */}
        {sections.cores && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>CORES VINCULADAS</Text>
            {liveCores && liveCores.length > 0 ? (
              <View style={styles.row}>
                {liveCores.map((color, idx) => (
                  <View key={idx} style={{ ...styles.col, minWidth: '25%', marginBottom: 8 }}>
                    <Text style={styles.value}>• {color.name || color.itemColorName || 'Cor não definida'}</Text>
                  </View>
                ))}
              </View>
            ) : data.productItemColors && data.productItemColors.length > 0 ? (
              <View style={styles.row}>
                {data.productItemColors.map((color, idx) => (
                  <View key={idx} style={{ ...styles.col, minWidth: '25%', marginBottom: 8 }}>
                    <Text style={styles.value}>• {color.itemColorName || 'Cor não definida'}</Text>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.value}>Nenhuma cor vinculada.</Text>
            )}
          </View>
        )}

        {/* SECTION: COMPOSIÇÃO */}
        {sections.composicao && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>COMPOSIÇÃO / INSUMOS</Text>
            {compositionItems && compositionItems.length > 0 ? (
              compositionItems.map((item, idx) => (
                <View key={idx} style={styles.compositionItem} wrap={false} minPresenceAhead={60}>
                  <View style={styles.row}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Insumo/Material</Text>
                      <Text style={styles.value}>{item.material || item.descricao || item.description || item.stock?.description || item.resource?.name || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Tipo</Text>
                      <Text style={styles.value}>{item.tipo || item.type || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Acabamento</Text>
                      <Text style={styles.value}>{item.finishing || item.acabamento || '-'}</Text>
                    </View>
                  </View>
                  <View style={{ ...styles.row, marginTop: 12 }}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Consumo</Text>
                      <Text style={styles.value}>{item.consumo || item.usage || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Un. Medida</Text>
                      <Text style={styles.value}>{item.unidadeMedida || item.unitOfMeasurement?.abbreviation || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Faixa de Tamanho</Text>
                      <Text style={styles.value}>{formatSizeRange(item.sizeRange)}</Text>
                    </View>
                  </View>
                  <View style={{ ...styles.row, marginTop: 12 }}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Custo/Un.</Text>
                      <Text style={styles.value}>{item.costPerUnit || item.cost || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}></View>
                    <View style={{ ...styles.col, flex: 1 }}></View>
                  </View>
                </View>
              ))
            ) : data.composition?.variations && data.composition.variations.length > 0 ? (
              data.composition.variations.map((item: any, idx) => (
                <View key={idx} style={styles.compositionItem} wrap={false} minPresenceAhead={60}>
                  <View style={styles.row}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Insumo/Material</Text>
                      <Text style={styles.value}>{item.description || item.stock?.description || item.resource?.name || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Tipo</Text>
                      <Text style={styles.value}>{item.type || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Acabamento</Text>
                      <Text style={styles.value}>{item.finishing || '-'}</Text>
                    </View>
                  </View>
                  <View style={{ ...styles.row, marginTop: 12 }}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Consumo</Text>
                      <Text style={styles.value}>{item.usage || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Un. Medida</Text>
                      <Text style={styles.value}>{item.unitOfMeasurement?.abbreviation || item.unit || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Faixa de Tamanho</Text>
                      <Text style={styles.value}>{formatSizeRange(item.sizeRange)}</Text>
                    </View>
                  </View>
                  <View style={{ ...styles.row, marginTop: 12 }}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Custo/Un.</Text>
                      <Text style={styles.value}>{item.cost || '-'}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}></View>
                    <View style={{ ...styles.col, flex: 1 }}></View>
                  </View>
                </View>
              ))
            ) : (
              <Text style={styles.value}>Nenhum insumo ou composição cadastrada.</Text>
            )}
          </View>
        )}

        {/* SECTION: MEDIDAS */}
        {sections.medidas && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>GRADE / MEDIDAS</Text>
            {data.gradeInProduct && data.gradeInProduct.length > 0 ? (
              <View style={styles.row}>
                <Text style={styles.value}>
                  Tamanhos: {data.gradeInProduct.map(g => g.grade?.name).join(', ')}
                </Text>
              </View>
            ) : (
              <Text style={styles.value}>Nenhuma medida ou grade cadastrada.</Text>
            )}
          </View>
        )}

        {/* SECTION: ANOTAÇÕES */}
        {sections.anotacoes && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>ANOTAÇÕES E OBSERVAÇÕES</Text>
            
            {/* Observações Gerais */}
            {(liveObservations || data.observations) && (
              <View style={{ marginBottom: 8 }}>
                <Text style={{ ...styles.label, marginBottom: 2 }}>Geral</Text>
                <Text style={styles.value}>{formatText(liveObservations || data.observations)}</Text>
              </View>
            )}

            {/* Notas adicionadas */}
            {liveAnotacoes && liveAnotacoes.length > 0 && liveAnotacoes.map((nota, idx) => (
              <View key={idx} style={{ marginBottom: 6 }}>
                <Text style={{ ...styles.label, marginBottom: 2 }}>{nota.title || `Nota ${idx + 1}`}</Text>
                <Text style={styles.value}>{formatText(nota.text || nota.description || nota.observacao)}</Text>
              </View>
            ))}

            {(!liveObservations && !data.observations && (!liveAnotacoes || liveAnotacoes.length === 0)) && (
              <Text style={styles.value}>Nenhuma anotação cadastrada.</Text>
            )}
          </View>
        )}

        {/* SECTION: APROVAÇÕES */}
        {sections.aprovacoes && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>APROVAÇÕES</Text>
            {liveAprovacoes && liveAprovacoes.length > 0 ? (
              liveAprovacoes.map((aprov, idx) => {
                const formatDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '-';
                return (
                  <View key={idx} style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }} wrap={false}>
                    <View style={{ ...styles.row, marginBottom: 8 }}>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Nome</Text>
                        <Text style={styles.value}>{aprov.name || '-'}</Text>
                      </View>
                      <View style={{ ...styles.col, flex: 0.8 }}>
                        <Text style={styles.label}>Status</Text>
                        <Text style={styles.value}>
                          {aprov.status === 'IN_PROGRESS' ? 'Em andamento' : 
                           aprov.status === 'APPROVED' ? 'Aprovado' : 
                           aprov.status === 'REJECTED' ? 'Reprovado' : aprov.status || '-'}
                        </Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Fornecedor</Text>
                        <Text style={styles.value}>{aprov.SupplierCompany?.companyName || '-'}</Text>
                      </View>
                    </View>
                    <View style={{ ...styles.row, marginBottom: 8 }}>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Criado por</Text>
                        <Text style={styles.value}>{aprov.CreatedBy?.name || '-'}</Text>
                      </View>
                      <View style={{ ...styles.col, flex: 0.8 }}>
                        <Text style={styles.label}>Aprovador</Text>
                        <Text style={styles.value}>{aprov.Approver?.name || '-'}</Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Data Início</Text>
                        <Text style={styles.value}>{formatDate(aprov.startDate)}</Text>
                      </View>
                    </View>
                    <View style={styles.row}>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Entrega Prevista</Text>
                        <Text style={styles.value}>{formatDate(aprov.expectedDeliveryDate)}</Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Valid./Tam.</Text>
                        <Text style={styles.value}>{(aprov.sizes ?? []).map((id: any) => resolveSizeName(id)).join(', ') || '-'}</Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}></View>
                    </View>
                  </View>
                )
              })
            ) : (
              <Text style={styles.value}>Nenhuma aprovação cadastrada.</Text>
            )}
          </View>
        )}

        <FooterOfPDF />
      </Page>
    </Document>
  )
}

export default TechnicalSheetPDF
